import React, { useState, useRef } from 'react';
import './App.css';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './components/ui/card';
import { Button } from './components/ui/button';
import { Input } from './components/ui/input';
import { Label } from './components/ui/label';
import { Textarea } from './components/ui/textarea';
import { Badge } from './components/ui/badge';
import { Separator } from './components/ui/separator';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from './components/ui/table';
import { 
  Upload, 
  TrendingUp, 
  DollarSign, 
  BarChart3, 
  FileText,
  AlertCircle,
  CheckCircle,
  Loader2,
  Download,
  RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function App() {
  const [tickerInput, setTickerInput] = useState('');
  const [analysisResult, setAnalysisResult] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [uploadedTickers, setUploadedTickers] = useState([]);
  const [userName, setUserName] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [assetType, setAssetType] = useState('stocks'); // 'stocks' or 'crypto'
  const fileInputRef = useRef(null);

  const clearAllInputs = () => {
    setTickerInput('');
    setUploadedTickers([]);
    setAnalysisResult(null);
    setUserName('');
    setUserEmail('');
    setAssetType('stocks');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    toast.success('Form cleared successfully');
  };

  const convertCryptoTickers = (tickers) => {
    if (assetType === 'crypto') {
      return tickers.map(ticker => {
        const upperTicker = ticker.toUpperCase();
        // If ticker doesn't already end with -USD, add it
        return upperTicker.endsWith('-USD') ? upperTicker : `${upperTicker}-USD`;
      });
    }
    return tickers;
  };

  const parseTickerInput = (input) => {
    return input
      .split(',')
      .map(ticker => ticker.trim().toUpperCase())
      .filter(ticker => ticker.length > 0);
  };

  const handleAnalyzePortfolio = async () => {
    // Validate required fields
    if (!userName.trim()) {
      toast.error('Please enter your name before proceeding');
      return;
    }
    
    if (!userEmail.trim()) {
      toast.error('Please enter your email before proceeding');
      return;
    }
    
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(userEmail)) {
      toast.error('Please enter a valid email address');
      return;
    }
    
    let tickers = tickerInput.trim() ? parseTickerInput(tickerInput) : uploadedTickers;
    
    if (tickers.length === 0) {
      toast.error(`Please enter ${assetType === 'crypto' ? 'crypto' : 'stock'} symbols or upload a file`);
      return;
    }

    // Convert crypto tickers if needed
    tickers = convertCryptoTickers(tickers);

    setIsAnalyzing(true);
    
    try {
      const response = await axios.post(`${API}/analyze-portfolio`, {
        tickers: tickers,
        name: userName.trim(),
        email: userEmail.trim(),
        asset_type: assetType
      });
      
      setAnalysisResult(response.data);
      toast.success(`Analysis complete for ${response.data.funds.length} funds`);
      
    } catch (error) {
      console.error('Analysis error:', error);
      if (error.response?.status === 404) {
        toast.error('No valid ticker data found. Please check your ticker symbols and try again.');
      } else if (error.response?.status === 500 && error.response?.data?.detail?.includes('No valid ticker data found')) {
        toast.error('No valid ticker data found. Please verify your ticker symbols.');
      } else {
        toast.error(error.response?.data?.detail || 'Analysis failed. Please try again.');
      }
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/upload-tickers`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setUploadedTickers(response.data.tickers);
      setTickerInput(''); // Clear manual input
      toast.success(`Loaded ${response.data.count} tickers from file`);
      
    } catch (error) {
      console.error('File upload error:', error);
      toast.error(error.response?.data?.detail || 'File upload failed');
    }
  };

  const getFlagColor = (flag) => {
    switch (flag) {
      case 'green': return 'bg-green-100 text-green-800 border-green-200';
      case 'yellow': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'red': return 'bg-red-100 text-red-800 border-red-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getFlagIcon = (flag) => {
    switch (flag) {
      case 'green': return <CheckCircle className="w-3 h-3" />;
      case 'yellow': return <AlertCircle className="w-3 h-3" />;
      case 'red': return <AlertCircle className="w-3 h-3" />;
      default: return null;
    }
  };

  const getReturnColor = (fundReturn, spyReturn) => {
    if (fundReturn === null || fundReturn === undefined || spyReturn === null || spyReturn === undefined) {
      return 'text-gray-600';
    }
    
    const difference = fundReturn - spyReturn;
    
    if (difference >= -0.4) {
      return 'text-green-600 font-semibold'; // Green for significantly outperforming SPY
    } else {
      return 'text-red-600 font-semibold'; // Red for below SPY performance
    }
  };

  const formatPercentage = (value) => {
    if (value === null || value === undefined) return '—';
    return `${value > 0 ? '+' : ''}${value.toFixed(2)}%`;
  };

  const formatExpenseRatio = (value) => {
    if (value === null || value === undefined) return '—';
    return `${value.toFixed(2)}%`;
  };

  const renderTickerCell = (fund) => {
    if (!fund.is_valid_ticker) {
      return (
        <div className="font-mono font-semibold text-black italic">
          <span>{fund.ticker}</span>
          <br />
          <span className="text-red-600 text-xs font-normal">— ⚠︎ Not Found</span>
        </div>
      );
    }
    
    const yahooFinanceUrl = `https://finance.yahoo.com/quote/${encodeURIComponent(fund.ticker)}`;
    return (
      <a 
        href={yahooFinanceUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="font-mono font-semibold text-blue-600 italic hover:text-blue-800 hover:underline transition-colors duration-200"
      >
        {fund.ticker}
      </a>
    );
  };

  const renderReturnCell = (fundReturn, spyReturn, isValidTicker) => {
    if (!isValidTicker) {
      return <span className="text-gray-500">—</span>;
    }
    
    const colorClass = getReturnColor(fundReturn, spyReturn);
    return <span className={colorClass}>{formatPercentage(fundReturn)}</span>;
  };

  const renderFlagCell = (flag, isValidTicker) => {
    if (!isValidTicker || flag === null || flag === undefined) {
      return <span className="text-gray-500">—</span>;
    }
    
    return (
      <Badge className={`${getFlagColor(flag)} gap-1 border`}>
        {getFlagIcon(flag)}
        {flag?.toUpperCase()}
      </Badge>
    );
  };

  const downloadCSV = () => {
    if (!analysisResult || !analysisResult.funds) {
      toast.error('No analysis data available to download');
      return;
    }

    try {
      // Create CSV headers
      const headers = [
        'Fund',
        'Ticker',
        'Expense Ratio (%)',
        '1-Yr Return (%)',
        '3-Yr Annualized (%)',
        '5-Yr Annualized (%)',
        'SPY 1-Yr (%)',
        'SPY 3-Yr (%)',
        'SPY 5-Yr (%)',
        'ROI Flag',
        'Fee Flag',
        '3-Yr Cumulative (%)',
        'SPY 3-Yr Cumulative (%)',
        '3-Yr vs SPY Flag',
        'Notes'
      ];

      // Create CSV rows
      const rows = analysisResult.funds.map(fund => [
        `"${(fund.fund || '').replace(/"/g, '""')}"`, // Escape quotes properly
        fund.ticker || '',
        fund.expense_ratio !== null && fund.expense_ratio !== undefined ? fund.expense_ratio.toFixed(2) : 'N/A',
        fund.one_year_return !== null && fund.one_year_return !== undefined ? fund.one_year_return.toFixed(2) : 'N/A',
        fund.three_year_annualized !== null && fund.three_year_annualized !== undefined ? fund.three_year_annualized.toFixed(2) : 'N/A',
        fund.five_year_annualized !== null && fund.five_year_annualized !== undefined ? fund.five_year_annualized.toFixed(2) : 'N/A',
        fund.spy_one_year !== null && fund.spy_one_year !== undefined ? fund.spy_one_year.toFixed(2) : 'N/A',
        fund.spy_three_year !== null && fund.spy_three_year !== undefined ? fund.spy_three_year.toFixed(2) : 'N/A',
        fund.spy_five_year !== null && fund.spy_five_year !== undefined ? fund.spy_five_year.toFixed(2) : 'N/A',
        (fund.roi_flag || 'N/A').toUpperCase(),
        (fund.fee_flag || 'N/A').toUpperCase(),
        fund.three_year_cumulative !== null && fund.three_year_cumulative !== undefined ? fund.three_year_cumulative.toFixed(2) : 'N/A',
        fund.spy_three_year_cumulative !== null && fund.spy_three_year_cumulative !== undefined ? fund.spy_three_year_cumulative.toFixed(2) : 'N/A',
        (fund.three_year_vs_spy_flag || 'N/A').toUpperCase(),
        `"${(fund.notes || '').replace(/"/g, '""')}"` // Escape quotes properly
      ]);

      // Combine headers and rows
      const csvContent = [headers, ...rows]
        .map(row => row.join(','))
        .join('\n');

      // Add BOM for Excel compatibility and analysis metadata
      const analysisDate = new Date(analysisResult.analysis_date).toLocaleDateString();
      const spyData = analysisResult.spy_benchmark_data || {};
      
      const formatValue = (val) => {
        if (val === null || val === undefined) return 'N/A';
        return `${val > 0 ? '+' : ''}${val.toFixed(2)}%`;
      };
      
      const metadata = `Portfolio Analysis - Journey To Wealth Method\nAnalysis Date: ${analysisDate}\nSPY Benchmark - 1Y: ${formatValue(spyData.one_year)} | 3Y: ${formatValue(spyData.three_year)} | 5Y: ${formatValue(spyData.five_year)}\nby Alicia Holmes\n\n`;
      
      const fullContent = '\ufeff' + metadata + csvContent;

      // Create and download file
      const blob = new Blob([fullContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `portfolio_analysis_jtw_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url); // Clean up

      toast.success('Portfolio analysis downloaded successfully');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download analysis. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-violet-50 to-purple-100">
      <div className="container mx-auto py-8 px-4 max-w-7xl">
        {/* Header */}
        <div className="text-center mb-12">
          {/* Hero Section with Gradient Background */}
          <div className="relative p-8 bg-gradient-to-r from-violet-100 via-purple-100 to-indigo-100 rounded-3xl shadow-lg mb-8">
            <div className="flex justify-center items-center gap-6 mb-6">
              <img 
                src="https://customer-assets.emergentagent.com/job_wealth-health/artifacts/yjtl30u3_Common%20Logo.png" 
                alt="Journey to Wealth Logo"
                className="w-20 h-20 rounded-full shadow-xl drop-shadow-2xl"
                style={{filter: 'drop-shadow(0 8px 16px rgba(139, 92, 246, 0.3))'}}
              />
              <div className="text-center">
                <h1 className="text-5xl font-bold bg-gradient-to-r from-purple-700 via-violet-600 to-purple-800 bg-clip-text text-transparent mb-2">
                  Portfolio Analysis
                </h1>
                <p className="text-xl text-gray-700 font-light italic">Journey To Wealth Method</p>
                <p className="text-sm text-gray-600 mt-1">by Alicia Holmes</p>
              </div>
            </div>
            
            {/* Descriptive Text with Better Spacing */}
            <div className="max-w-3xl mx-auto">
              <p className="text-xl text-gray-700 leading-relaxed font-semibold">
                Track, compare, and grow smarter.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed mt-3">
                Easily check how your funds are performing — see returns, fees, and how they stack up against the S&P 500
              </p>
            </div>
          </div>
        </div>

        {/* Input Section */}
        <Card className="mb-8 shadow-2xl border-0 bg-white/80 backdrop-blur-md rounded-3xl overflow-hidden">
          <CardHeader className="bg-gradient-to-r from-purple-50 to-violet-50 pb-6">
            <CardTitle className="flex items-center justify-center gap-3 text-2xl mb-4">
              <div className="text-3xl">💜</div>
              <span className="font-bold text-gray-800">Enter Your Fund Tickers</span>
            </CardTitle>
            <CardDescription className="text-center text-gray-600 text-lg">
              Discover your portfolio's performance using the Journey To Wealth Method
            </CardDescription>
          </CardHeader>
          
          <CardContent className="p-8 space-y-8">
            {/* Asset Type Toggle */}
            <div className="flex justify-center mb-8">
              <div className="bg-white p-2 rounded-2xl shadow-lg border-2 border-purple-200">
                <div className="flex gap-2">
                  <Button
                    onClick={() => setAssetType('stocks')}
                    className={`px-6 py-3 rounded-xl font-semibold transition-all duration-200 ${
                      assetType === 'stocks'
                        ? 'bg-gradient-to-r from-purple-600 to-violet-600 text-white shadow-md'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                    data-testid="stocks-toggle"
                  >
                    📈 Stocks & Funds
                  </Button>
                  <Button
                    onClick={() => setAssetType('crypto')}
                    className={`px-6 py-3 rounded-xl font-semibold transition-all duration-200 ${
                      assetType === 'crypto'
                        ? 'bg-gradient-to-r from-orange-500 to-amber-600 text-white shadow-md'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                    data-testid="crypto-toggle"
                  >
                    ₿ Cryptocurrency
                  </Button>
                </div>
              </div>
            </div>

            {/* Required User Information */}
            <div className="p-6 bg-gradient-to-r from-purple-50 to-violet-50 rounded-2xl border-2 border-purple-200 mb-8">
              <h3 className="text-lg font-bold text-purple-800 mb-2">
                Let's Get Started
              </h3>
              <p className="text-sm text-purple-600 mb-4">
                Enter your details below to begin your portfolio review.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="userName" className="text-sm font-semibold text-purple-800">
                    Name <span className="text-purple-600">*</span>
                  </Label>
                  <Input
                    id="userName"
                    data-testid="user-name-input"
                    placeholder="Enter your full name"
                    value={userName}
                    onChange={(e) => setUserName(e.target.value)}
                    className="rounded-xl border-2 border-purple-200 focus:border-purple-400 transition-colors"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="userEmail" className="text-sm font-semibold text-purple-800">
                    Email <span className="text-purple-600">*</span>
                  </Label>
                  <Input
                    id="userEmail"
                    type="email"
                    data-testid="user-email-input"
                    placeholder="Enter your email address"
                    value={userEmail}
                    onChange={(e) => setUserEmail(e.target.value)}
                    className="rounded-xl border-2 border-purple-200 focus:border-purple-400 transition-colors"
                    required
                  />
                </div>
              </div>
            </div>

            {/* Step-by-step Visual Instructions */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              <div className="flex items-center gap-4 p-4 bg-gradient-to-r from-purple-50 to-violet-50 rounded-2xl border border-purple-100">
                <div className="text-2xl font-bold text-purple-600 bg-white rounded-full w-10 h-10 flex items-center justify-center shadow-md">
                  1️⃣
                </div>
                <div>
                  <h3 className="font-semibold text-gray-800">Enter ticker symbols manually</h3>
                  <p className="text-sm text-gray-600">Type comma-separated tickers below</p>
                </div>
              </div>
              
              <div className="flex items-center gap-4 p-4 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-2xl border border-purple-100">
                <div className="text-2xl font-bold text-purple-600 bg-white rounded-full w-10 h-10 flex items-center justify-center shadow-md">
                  2️⃣
                </div>
                <div>
                  <h3 className="font-semibold text-gray-800">Or upload a file with ticker symbols</h3>
                  <p className="text-sm text-gray-600">Upload .txt/.csv with ticker symbols only</p>
                </div>
              </div>
            </div>

            {/* Manual Input with Modern Styling */}
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <Label htmlFor="tickers" className="text-lg font-semibold text-gray-800">
                  Ticker Symbols (comma-separated)
                </Label>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={clearAllInputs}
                  className="gap-2 text-gray-600 hover:text-gray-800 border-gray-300 hover:border-gray-400"
                  data-testid="refresh-button"
                >
                  <RefreshCw className="w-4 h-4" />
                  Clear All
                </Button>
              </div>
              <Textarea
                id="tickers"
                data-testid="ticker-input"
                placeholder="Enter ticker symbols separated by commas (e.g., VEMPX, VIGIX, VMGMX)"
                value={tickerInput}
                onChange={(e) => setTickerInput(e.target.value)}
                className="min-h-[120px] resize-none rounded-2xl bg-gradient-to-r from-gray-50 to-purple-50 border-2 border-purple-200 hover:border-purple-300 focus:border-purple-400 transition-all duration-200 shadow-md hover:shadow-lg text-lg p-4"
                disabled={uploadedTickers.length > 0}
                style={{
                  background: uploadedTickers.length > 0 
                    ? 'linear-gradient(to right, #f3f4f6, #faf5ff)' 
                    : 'linear-gradient(to right, #f9fafb, #faf5ff)'
                }}
              />
              {uploadedTickers.length > 0 && (
                <div className="p-4 bg-gradient-to-r from-purple-100 to-violet-100 rounded-xl border border-purple-200">
                  <p className="text-sm text-purple-700 font-medium">
                    📁 Using {uploadedTickers.length} tickers from uploaded file. Clear file to use manual input.
                  </p>
                </div>
              )}
            </div>

            <div className="flex items-center gap-4">
              <Separator className="flex-1 bg-gradient-to-r from-purple-200 to-transparent" />
              <span className="text-sm text-gray-500 font-medium px-4 py-2 bg-purple-50 rounded-full">OR</span>
              <Separator className="flex-1 bg-gradient-to-l from-purple-200 to-transparent" />
            </div>

            {/* File Upload */}
            <div className="space-y-4">
              <Label className="text-lg font-semibold text-gray-800">Upload File (.txt or .csv)</Label>
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg mb-3">
                <p className="text-sm text-blue-800 font-medium mb-1">📄 File Format Requirements:</p>
                <p className="text-xs text-blue-700">
                  • <strong>Ticker symbols only</strong> (e.g., SPY, VTI, AAPL)
                </p>
                <p className="text-xs text-blue-700">
                  • <strong>Do not include</strong> fund names or descriptions
                </p>
                <p className="text-xs text-blue-700">
                  • Separate tickers with commas or new lines
                </p>
              </div>
              <div className="flex items-center gap-6">
                <Button
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  className="gap-3 px-6 py-3 rounded-2xl border-2 border-purple-200 hover:border-purple-300 hover:bg-purple-50 transition-all duration-200 shadow-md hover:shadow-lg"
                  data-testid="upload-file-btn"
                >
                  <Upload className="w-5 h-5 text-purple-600" />
                  <span className="font-medium">Choose File</span>
                </Button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".txt,.csv"
                  onChange={handleFileUpload}
                  className="hidden"
                  data-testid="file-input"
                />
                {uploadedTickers.length > 0 && (
                  <div className="flex items-center gap-3">
                    <Badge variant="secondary" className="gap-2 px-4 py-2 bg-gradient-to-r from-green-100 to-emerald-100 text-green-800 border border-green-200 rounded-xl">
                      <FileText className="w-4 h-4" />
                      {uploadedTickers.length} tickers loaded
                    </Badge>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setUploadedTickers([]);
                        if (fileInputRef.current) fileInputRef.current.value = '';
                      }}
                      className="text-gray-500 hover:text-red-600 transition-colors duration-200"
                      data-testid="clear-uploaded-tickers"
                    >
                      Clear
                    </Button>
                  </div>
                )}
              </div>
            </div>

            {/* Current Tickers Preview */}
            {(tickerInput.trim() || uploadedTickers.length > 0) && (
              <div className="p-6 bg-gradient-to-r from-purple-50 via-violet-50 to-indigo-50 rounded-2xl border-2 border-purple-200 shadow-md">
                <p className="text-lg font-semibold text-purple-800 mb-4 flex items-center gap-2">
                  📊 Tickers to analyze:
                </p>
                <div className="flex flex-wrap gap-3">
                  {(uploadedTickers.length > 0 ? uploadedTickers : parseTickerInput(tickerInput)).map((ticker, index) => (
                    <Badge key={index} className="bg-gradient-to-r from-purple-600 to-violet-600 text-white px-4 py-2 rounded-xl font-medium shadow-md">
                      {ticker}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Enhanced Call-to-Action Button */}
            <div className="pt-4">
              <Button
                onClick={handleAnalyzePortfolio}
                disabled={isAnalyzing || (tickerInput.trim() === '' && uploadedTickers.length === 0) || !userName.trim() || !userEmail.trim()}
                className="w-full bg-gradient-to-r from-purple-700 via-violet-600 to-purple-800 hover:from-purple-800 hover:via-violet-700 hover:to-purple-900 text-white font-bold py-4 px-8 rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 text-lg disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="analyze-portfolio-btn"
              >
                {isAnalyzing ? (
                  <div className="flex items-center gap-3">
                    <Loader2 className="w-6 h-6 animate-spin" />
                    <span>Analyzing Your Portfolio...</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-3">
                    <span>⚡</span>
                    <span>Run My Portfolio Analysis</span>
                    <BarChart3 className="w-6 h-6" />
                  </div>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Results Section */}
        {analysisResult && (
          <Card className="shadow-2xl border-0 bg-white/80 backdrop-blur-md rounded-3xl overflow-hidden">
            <CardHeader className="bg-gradient-to-r from-purple-50 to-violet-50">
              <div className="flex justify-between items-start">
                <div>
                  <CardTitle className="flex items-center gap-3 text-2xl mb-2">
                    <div className="p-2 bg-gradient-to-r from-green-500 to-emerald-500 rounded-xl shadow-lg">
                      <BarChart3 className="w-6 h-6 text-white" />
                    </div>
                    Portfolio Analysis Results
                  </CardTitle>
                  <CardDescription className="text-lg">
                    Analysis completed on {new Date(analysisResult.analysis_date).toLocaleDateString()} 
                    {analysisResult.spy_benchmark_data && (
                      <span className="ml-2 text-gray-600">
                        | SPY Benchmark: 1Y: {formatPercentage(analysisResult.spy_benchmark_data.one_year)}, 
                        3Y: {formatPercentage(analysisResult.spy_benchmark_data.three_year)}, 
                        5Y: {formatPercentage(analysisResult.spy_benchmark_data.five_year)}
                      </span>
                    )}
                  </CardDescription>
                </div>
                <Button
                  onClick={downloadCSV}
                  className="gap-2 bg-gradient-to-r from-purple-600 to-violet-600 hover:from-purple-700 hover:to-violet-700 text-white shadow-lg hover:shadow-xl transition-all duration-200 rounded-xl px-6 py-3"
                  data-testid="download-csv-btn"
                >
                  <Download className="w-4 h-4" />
                  Download CSV
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="scroll-container relative" 
                style={{
                  overflowX: 'auto',
                  maxWidth: '100%'
                }}>
                {/* Horizontal Scroll Indicator */}
                <div className="flex justify-between items-center mb-4 text-xs text-gray-500 bg-gray-50 p-2 rounded">
                  <span className="flex items-center gap-2">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16l-4-4m0 0l4-4m-4 4h18" />
                    </svg>
                    Scroll horizontally to see all columns
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                    </svg>
                  </span>
                  <span className="bg-gray-200 px-2 py-1 rounded text-xs">Shift + scroll wheel for horizontal scroll</span>
                </div>
                
                <Table className="relative"
                  style={{
                    background: 'white',
                    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
                  }}
                >
                  <TableHeader className="sticky top-0 z-10 bg-white shadow-sm">
                    <TableRow className="bg-gradient-to-r from-purple-50 to-violet-50">
                      <TableHead className="font-semibold sticky top-0 bg-purple-50 min-w-[200px]">Fund</TableHead>
                      <TableHead className="font-semibold sticky top-0 bg-purple-50 min-w-[80px]">Ticker</TableHead>
                      <TableHead className="font-semibold sticky top-0 bg-purple-50 min-w-[100px]">Expense Ratio</TableHead>
                      <TableHead className="font-semibold sticky top-0 bg-purple-50 min-w-[120px]">1-Yr Return %</TableHead>
                      <TableHead className="font-semibold sticky top-0 bg-purple-50 min-w-[120px]">3-Yr Returns %</TableHead>
                      <TableHead className="font-semibold sticky top-0 bg-purple-50 min-w-[120px]">5-Yr Returns %</TableHead>
                      <TableHead className="font-semibold sticky top-0 bg-purple-50 min-w-[100px]">SPY 1-Yr %</TableHead>
                      <TableHead className="font-semibold sticky top-0 bg-purple-50 min-w-[100px]">SPY 3-Yr %</TableHead>
                      <TableHead className="font-semibold sticky top-0 bg-purple-50 min-w-[100px]">SPY 5-Yr %</TableHead>
                      <TableHead className="font-semibold sticky top-0 bg-purple-50 min-w-[80px]">ROI Flag</TableHead>
                      <TableHead className="font-semibold sticky top-0 bg-purple-50 min-w-[80px]">Fee Flag</TableHead>
                      <TableHead className="font-semibold sticky top-0 bg-purple-50 min-w-[140px]">3-Yr Cumulative %</TableHead>
                      <TableHead className="font-semibold sticky top-0 bg-purple-50 min-w-[160px]">SPY 3-Yr Cumulative %</TableHead>
                      <TableHead className="font-semibold sticky top-0 bg-purple-50 min-w-[120px]">3-Yr vs SPY Flag</TableHead>
                      <TableHead className="font-semibold sticky top-0 bg-purple-50 min-w-[400px]">Notes</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {analysisResult.funds.map((fund, index) => (
                      <TableRow key={index} className="hover:bg-gray-50/50 transition-colors">
                        <TableCell className="font-medium max-w-[200px] truncate" title={fund.fund}>
                          {fund.fund}
                        </TableCell>
                        <TableCell>
                          {renderTickerCell(fund)}
                        </TableCell>
                        <TableCell>{fund.is_valid_ticker ? formatExpenseRatio(fund.expense_ratio) : '—'}</TableCell>
                        <TableCell>
                          {renderReturnCell(fund.one_year_return, fund.spy_one_year, fund.is_valid_ticker)}
                        </TableCell>
                        <TableCell>
                          {renderReturnCell(fund.three_year_annualized, fund.spy_three_year, fund.is_valid_ticker)}
                        </TableCell>
                        <TableCell>
                          {renderReturnCell(fund.five_year_annualized, fund.spy_five_year, fund.is_valid_ticker)}
                        </TableCell>
                        <TableCell className="text-black font-medium">
                          {fund.is_valid_ticker ? formatPercentage(fund.spy_one_year) : '—'}
                        </TableCell>
                        <TableCell className="text-black font-medium">
                          {fund.is_valid_ticker ? formatPercentage(fund.spy_three_year) : '—'}
                        </TableCell>
                        <TableCell className="text-black font-medium">
                          {fund.is_valid_ticker ? formatPercentage(fund.spy_five_year) : '—'}
                        </TableCell>
                        <TableCell>
                          {renderFlagCell(fund.roi_flag, fund.is_valid_ticker)}
                        </TableCell>
                        <TableCell>
                          {renderFlagCell(fund.fee_flag, fund.is_valid_ticker)}
                        </TableCell>
                        <TableCell>
                          {renderReturnCell(fund.three_year_cumulative, fund.spy_three_year_cumulative, fund.is_valid_ticker)}
                        </TableCell>
                        <TableCell className="text-black font-medium">
                          {fund.is_valid_ticker ? formatPercentage(fund.spy_three_year_cumulative) : '—'}
                        </TableCell>
                        <TableCell>
                          {renderFlagCell(fund.three_year_vs_spy_flag, fund.is_valid_ticker)}
                        </TableCell>
                        <TableCell className="text-sm text-gray-600 max-w-[400px] min-w-[400px] whitespace-normal break-words leading-relaxed">
                          {fund.notes}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Legend Section */}
              <div className="mt-6 p-4 bg-gradient-to-r from-gray-50 to-purple-50 rounded-lg border border-purple-100">
                <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 text-purple-600" />
                  Color-Coded Flag Legend - Journey To Wealth Method
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* ROI Flag Legend */}
                  <div className="space-y-3">
                    <h4 className="font-semibold text-gray-700">Overall Performance Rating</h4>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <Badge className="bg-green-100 text-green-800 border-green-200 gap-1 border">
                          <CheckCircle className="w-3 h-3" />
                          GREEN
                        </Badge>
                        <span className="text-sm text-gray-600">Strong performer - Recommended holding</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className="bg-yellow-100 text-yellow-800 border-yellow-200 gap-1 border">
                          <AlertCircle className="w-3 h-3" />
                          YELLOW
                        </Badge>
                        <span className="text-sm text-gray-600">Mixed performance - Monitor closely</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className="bg-red-100 text-red-800 border-red-200 gap-1 border">
                          <AlertCircle className="w-3 h-3" />
                          RED
                        </Badge>
                        <span className="text-sm text-gray-600">Underperforming - Consider alternatives</span>
                      </div>
                    </div>
                  </div>

                  {/* Fee Flag Legend */}
                  <div className="space-y-3">
                    <h4 className="font-semibold text-gray-700">Fee Analysis (Expense Ratio)</h4>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <Badge className="bg-green-100 text-green-800 border-green-200 gap-1 border">
                          <CheckCircle className="w-3 h-3" />
                          GREEN
                        </Badge>
                        <span className="text-sm text-gray-600">Low fees - Great for long-term growth</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className="bg-yellow-100 text-yellow-800 border-yellow-200 gap-1 border">
                          <AlertCircle className="w-3 h-3" />
                          YELLOW
                        </Badge>
                        <span className="text-sm text-gray-600">Moderate fees - Acceptable range</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className="bg-red-100 text-red-800 border-red-200 gap-1 border">
                          <AlertCircle className="w-3 h-3" />
                          RED
                        </Badge>
                        <span className="text-sm text-gray-600">High fees - Consider lower-cost alternatives</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className="bg-gray-100 text-gray-600 border-gray-200 gap-1 border">
                          N/A
                        </Badge>
                        <span className="text-sm text-gray-600">No fees apply (stocks/ETFs)</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-4 pt-4 border-t border-purple-200">
                  {/* Calculation Methodology Note */}
                  <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <h4 className="text-sm font-semibold text-gray-800 mb-3">📊 Calculation Methodology</h4>
                    <div className="text-xs text-gray-600 space-y-2">
                      <p className="font-medium">These are trailing returns manually calculated using historical price data and the following formulas:</p>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-3">
                        <div className="bg-white p-3 rounded border">
                          <p className="font-mono text-xs font-semibold text-blue-700">1-Year Return:</p>
                          <p className="font-mono text-xs">(current_price / price_252_days_ago) - 1</p>
                        </div>
                        <div className="bg-white p-3 rounded border">
                          <p className="font-mono text-xs font-semibold text-blue-700">3-Year Return:</p>
                          <p className="font-mono text-xs">((current_price / price_756_days_ago) ^ (1/3)) - 1</p>
                        </div>
                        <div className="bg-white p-3 rounded border">
                          <p className="font-mono text-xs font-semibold text-blue-700">5-Year Return:</p>
                          <p className="font-mono text-xs">((current_price / price_1260_days_ago) ^ (1/5)) - 1</p>
                        </div>
                      </div>
                      <p className="text-xs text-gray-500 mt-3 italic">
                        * Calculations use the most recent quarter-end date as the current price reference point.
                        Trading days are approximated as 252 per year for historical lookback periods.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
      
      {/* Disclaimer - Always at the bottom */}
      <div className="mt-12 mb-8 px-4 max-w-7xl mx-auto">
        <div className="p-6 bg-gray-100 border-2 border-gray-300 rounded-lg">
          <h4 className="text-sm font-bold text-gray-800 mb-3">⚠️ Important Disclaimer</h4>
          <p className="text-xs text-gray-700 leading-relaxed">
            The Portfolio Assessment Tool and AI Prompts are provided for informational and educational purposes only. 
            They do not constitute financial, investment, legal, or tax advice. The prompts and examples illustrate how AI 
            can support research and decision-making but should not replace professional guidance or independent analysis.
          </p>
        </div>
      </div>
    </div>
  );
}

export default App;